import React, { useMemo, useState } from "react";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { IBreed, IDog, IUser } from "@eagerdog/interfaces";

import { apiService } from "src/services";
import { helperService } from "src/services/helper.service";
import { toast } from "src/components/Toast/ToastManager";
import { Modal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import Input from "src/components/Input/Input";
import Dropdown from "src/components/Dropdown/Dropdown";
import OptionButtons, { fitsOptionButtons } from "src/components/OptionButtons/OptionButtons";
import DatePick from "src/components/DatePick/DatePick";
import SearchDown from "src/components/SearchDown/SearchDown";

import {
  RegistrationContext,
  RegistrationField,
  buildSavePayload,
  effectiveDog,
  isPalRegistrationType,
  persistDogUpdate,
  registrationTypeOptionsFor,
  resolveMissingFieldsForLevels,
} from "src/registration-requirements";

import "./MissingFieldsModal.scss";

interface IProps {
  context: RegistrationContext;
  fields: RegistrationField[];
  levels?: string[];
  isAdmin?: boolean;
  eventId?: string;
  onSaved: (dog: IDog) => void;
  onProgress?: (dog: IDog) => void;
  onCancel: () => void;
}

const pedigreeIds = ["sire", "dam", "breeder"];

const showTypeLabel = (showType: string) => showType.replace(/_/g, " ");

const presentText = (value?: string | null) => {
  const text = value?.trim();
  if (!text || text === "None") return "";
  return text;
};

const withSavedPayload = (dog: IDog, saved: Record<string, unknown>): IDog => {
  const next: IDog = { ...dog };
  if (Array.isArray(saved.club_registrations)) {
    next.club_registrations = saved.club_registrations as IDog["club_registrations"];
  }
  (["dob", "height_at_withers", "sire", "dam", "breeder", "final_response"] as const).forEach((key) => {
    if (key in saved) {
      next[key] = saved[key] as never;
    }
  });
  return next;
};

const asDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const MissingFieldsModal: React.FC<IProps> = (props) => {
  const user: IUser = helperService.getUser();
  const [ctx, setCtx] = useState(props.context);
  const [activeFields, setActiveFields] = useState(props.fields);
  const [levels, setLevels] = useState(props.levels);
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {};
    props.fields.forEach((field) => {
      initial[field.id] = field.read(props.context);
    });
    return initial;
  });
  const [breedOptions, setBreedOptions] = useState<Record<string, string[]>>({});
  const [breedSearch, setBreedSearch] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showError, setShowError] = useState(false);

  const registrationDraft = values.registration as { registration_type?: string } | undefined;
  const hidePedigree = isPalRegistrationType(registrationDraft?.registration_type);
  const visibleFields = hidePedigree
    ? activeFields.filter((field) => !pedigreeIds.includes(field.id))
    : activeFields;

  const breedClubs = useMemo(
    () => visibleFields
      .filter((field) => field.input.kind === "breed")
      .map((field) => (field.input.kind === "breed" ? field.input.sanctioningClub : "")),
    [visibleFields],
  );

  React.useEffect(() => {
    let cancelled = false;
    breedClubs.forEach((club) => {
      if (!club || breedOptions[club]) return;
      apiService.getBreedsBySanctioningClub(club).then((response: IBreed[]) => {
        if (cancelled) return;
        setBreedOptions((prev) => ({
          ...prev,
          [club]: response.map((breed) => breed.breed),
        }));
      }).catch(() => {
        toast.show({
          title: "Dog Breeds",
          content: "Unable to get dog breeds",
          duration: 10000,
          type: "fail",
        });
      });
    });
    return () => { cancelled = true; };
  }, [breedClubs, breedOptions]);

  const dog = effectiveDog(ctx);
  const clubRegistration = dog.club_registrations?.find((registration) => registration.sanctioning_club === ctx.sanctioningClub);
  const callName = clubRegistration?.call_name || dog.call_name;
  const registeredName = presentText(clubRegistration?.registered_name) || presentText(ctx.proposedChanges?.registered_name?.new) || presentText(dog.registered_name);
  const breedLine = [
    presentText(clubRegistration?.breed) || presentText(dog.breed),
    presentText(clubRegistration?.variety) || presentText(ctx.proposedChanges?.variety?.new) || presentText(dog.variety),
    presentText(clubRegistration?.variation),
  ].filter(Boolean).join(" - ");
  const dobLabel = dog.dob ? moment(dog.dob).format("MMM D, YYYY") : "";
  const levelLabel = (levels && levels.length === 1 ? levels[0] : ctx.level) || "";

  const setValue = (id: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [id]: value }));
  };

  const valueMissing = (field: RegistrationField) => {
    const value = values[field.id];
    if (field.input.kind === "registration") {
      const registration = value as { registration_type?: string; registration_number?: string } | undefined;
      return !registration?.registration_type || !registration.registration_number?.trim();
    }
    if (field.input.kind === "number") {
      const height = typeof value === "number" ? value : parseFloat(String(value ?? ""));
      return !height || height <= 0;
    }
    if (field.input.kind === "date") {
      return !asDate(value);
    }
    if (field.input.kind === "select") {
      return !value || value === "None";
    }
    return !String(value ?? "").trim();
  };

  const save = async () => {
    if (visibleFields.some(valueMissing)) {
      setShowError(true);
      return;
    }

    const payload = buildSavePayload(ctx, values, visibleFields);
    if (Object.keys(payload).length === 0) {
      setShowError(true);
      return;
    }

    setSaving(true);
    setShowError(false);
    try {
      const updated = await persistDogUpdate({
        dog: ctx.dog,
        userId: user._id,
        isAdmin: props.isAdmin,
        eventId: props.eventId,
        proposedChanges: ctx.proposedChanges,
        payload,
      });

      const nextProposed = props.isAdmin && props.eventId
        ? updated.proposed_changes?.find((change) => change.event_id?.toString() === props.eventId?.toString())
        : ctx.proposedChanges;
      const savedDog = nextProposed ? updated : withSavedPayload(updated, payload);
      const nextCtx: RegistrationContext = {
        ...ctx,
        dog: savedDog,
        proposedChanges: nextProposed,
      };
      const stillMissing = resolveMissingFieldsForLevels(nextCtx, levels?.length ? levels : (nextCtx.level ? [nextCtx.level] : []));

      if (stillMissing.length > 0) {
        setCtx(nextCtx);
        setActiveFields(stillMissing);
        setLevels(levels);
        const nextValues: Record<string, unknown> = {};
        stillMissing.forEach((field) => {
          nextValues[field.id] = values[field.id] ?? field.read(nextCtx);
        });
        setValues(nextValues);
        props.onProgress?.(savedDog);
        return;
      }

      props.onSaved(savedDog);
    } catch {
      toast.show({
        title: "Edit Dog",
        content: "Unable to update dog to account",
        duration: 10000,
        type: "fail",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: RegistrationField) => {
    if (field.input.kind === "registration") {
      const registration = (values.registration || {}) as { registration_type?: string; registration_number?: string };
      const options = registrationTypeOptionsFor(ctx.sanctioningClub, ctx.show.show_type);
      const selected = options.find((option) => option.id === registration.registration_type) || { id: "", value: "" };
      const numberLabel = ctx.sanctioningClub === Constants.sanctioning_club.PWDCA
        ? "CKC or AKC registration number"
        : `${ctx.sanctioningClub} registration number`;
      return (
        <div key={field.id} className="field">
          <Dropdown
            label="Registration type"
            options={options}
            placeholder="Select a type"
            value={selected}
            onChange={(_event, option) => {
              setValues((prev) => {
                const current = (prev.registration || {}) as { registration_type?: string; registration_number?: string };
                return { ...prev, registration: { ...current, registration_type: option.id } };
              });
            }}
          />
          <Input
            label={numberLabel}
            type="text"
            defaultValue={registration.registration_number || ""}
            onChange={(event) => {
              const number = event.target.value;
              setValues((prev) => {
                const current = (prev.registration || {}) as { registration_type?: string; registration_number?: string };
                return { ...prev, registration: { ...current, registration_number: number } };
              });
            }}
          />
        </div>
      );
    }

    if (field.input.kind === "select") {
      const current = String(values[field.id] ?? "");
      const options = field.input.options;
      const selected = options.find((option) => option.id === current) || { id: "", value: "" };
      // A short list is one tap per option; longer lists stay in a Dropdown.
      return (
        <div key={field.id} className="field">
          {fitsOptionButtons(options) ? (
            <OptionButtons
              label={field.label}
              options={options}
              value={selected}
              onChange={(_event, option) => setValue(field.id, option.id)}
            />
          ) : (
            <Dropdown
              label={field.label}
              options={options}
              placeholder="Select"
              value={selected}
              onChange={(_event, option) => setValue(field.id, option.id)}
            />
          )}
        </div>
      );
    }

    if (field.input.kind === "date") {
      return (
        <div key={field.id} className="field">
          <DatePick
            label={field.label}
            maxDate={new Date()}
            value={asDate(values[field.id])}
            onChange={(date: Date) => setValue(field.id, date)}
          />
        </div>
      );
    }

    if (field.input.kind === "number") {
      return (
        <div key={field.id} className="field">
          <Input
            label={field.label}
            type="number"
            step={field.input.step}
            min={field.input.min}
            defaultValue={values[field.id] === undefined || values[field.id] === null ? "" : String(values[field.id])}
            onChange={(event) => setValue(field.id, event.target.value)}
          />
        </div>
      );
    }

    if (field.input.kind === "breed") {
      const club = field.input.sanctioningClub;
      const current = String(values[field.id] ?? "");
      const search = breedSearch[field.id] ?? "";
      const results = (breedOptions[club] || []).filter((breed) => breed.toLowerCase().includes((search || current).toLowerCase())).slice(0, 20);
      return (
        <div key={field.id} className="field">
          <SearchDown
            label={field.label}
            defaultValue={current}
            searchResults={results}
            onSearch={(term) => setBreedSearch((prev) => ({ ...prev, [field.id]: term }))}
            onChange={(event) => {
              setBreedSearch((prev) => ({ ...prev, [field.id]: event.target.value }));
              setValue(field.id, event.target.value);
            }}
            onSelect={(breed) => {
              setBreedSearch((prev) => ({ ...prev, [field.id]: "" }));
              setValue(field.id, breed);
            }}
          />
        </div>
      );
    }

    return (
      <div key={field.id} className="field">
        <Input
          label={field.label}
          type="text"
          defaultValue={String(values[field.id] ?? "")}
          onChange={(event) => setValue(field.id, event.target.value)}
        />
      </div>
    );
  };

  return (
    <Modal
      className="missingFields"
      isShown={true}
      hide={props.onCancel}
      modalContent={(
        <div className="missingFieldsBody">
          <h2>{callName}</h2>
          {registeredName && <div className="registeredName">{registeredName}</div>}
          {(breedLine || dobLabel) && (
            <div className="dogPills">
              {breedLine && <span className="pill breed">{breedLine}</span>}
              {dobLabel && <span className="pill born">Born {dobLabel}</span>}
            </div>
          )}
          <p className="prompt">
            Enter the details needed for this {ctx.sanctioningClub} {showTypeLabel(ctx.show.show_type)}
            {levelLabel ? ` ${levelLabel}` : ""} entry.
          </p>
          {visibleFields.map(renderField)}
          {showError && <div className="formError">Enter each field before saving.</div>}
          <div className="actions">
            <Button variant="secondary" onClick={props.onCancel} disabled={saving}>Cancel</Button>
            <Button onClick={save} isLoading={saving}>Save</Button>
          </div>
        </div>
      )}
    />
  );
};

export default MissingFieldsModal;
